import { alpha, keyframes, type SxProps, type Theme } from "@mui/material/styles";

type StepState = "done" | "current" | "upcoming";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ---------- Page frame ----------

export const root: SxProps<Theme> = (theme) => ({
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  color: "text.primary",
  // A soft brand-tinted band behind the hero, fading into the page background.
  background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.09)} 0px, ${theme.palette.background.default} 340px)`,
  bgcolor: "background.default",
});

export const header: SxProps<Theme> = {
  height: 60,
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: { xs: 2, sm: 4 },
};

export const headerLogo: SxProps<Theme> = {
  height: 24,
  width: "auto",
  objectFit: "contain",
};

export const secureBadge: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.625,
  px: 1.25,
  py: 0.5,
  borderRadius: 999,
  bgcolor: alpha(theme.palette.background.paper, 0.85),
  border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: "success.main",
});

export const main: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  justifyContent: "center",
  px: { xs: 2, sm: 3 },
  pt: { xs: 1, sm: 2 },
  pb: { xs: 12, md: 6 },
};

export const mainInner: SxProps<Theme> = {
  width: "100%",
  maxWidth: 960,
};

// ---------- Store hero ----------

export const storeHero: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: { xs: 1.5, sm: 2 },
  mb: { xs: 2, md: 3 },
  p: { xs: 1.75, sm: 2.25 },
  borderRadius: 2.75,
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  flexWrap: { xs: "wrap", sm: "nowrap" },
});

export const storeLogo =
  (size: number): SxProps<Theme> =>
  (theme) => ({
    width: size,
    height: size,
    borderRadius: 1.75,
    objectFit: "cover",
    flex: "none",
    boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.divider}`,
  });

export const storeLogoFallback =
  (size: number): SxProps<Theme> =>
  (theme) => ({
    width: size,
    height: size,
    borderRadius: 1.75,
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: size * 0.42,
    color: "primary.contrastText",
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  });

export const heroEyebrow: SxProps<Theme> = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "text.secondary",
};

export const heroStoreName: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "1rem", sm: "1.125rem" },
  lineHeight: 1.5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const heroMeta: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
};

export const heroAmount: SxProps<Theme> = (theme) => ({
  flex: { xs: "1 1 100%", sm: "none" },
  display: "flex",
  flexDirection: { xs: "row", sm: "column" },
  alignItems: { xs: "center", sm: "flex-end" },
  justifyContent: "space-between",
  gap: 0.25,
  px: { xs: 1.5, sm: 2 },
  py: { xs: 1, sm: 1.25 },
  borderRadius: 2,
  bgcolor: alpha(theme.palette.primary.main, 0.07),
});

export const heroAmountLabel: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const heroAmountValue: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
  color: "primary.main",
  whiteSpace: "nowrap",
};

// ---------- Layout and cards ----------

export const layout: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" },
  gap: { xs: 2, md: 2.75 },
  alignItems: "start",
};

export const mainColumn: SxProps<Theme> = {
  order: { xs: 2, md: 1 },
  minWidth: 0,
};

export const summaryColumn: SxProps<Theme> = {
  order: { xs: 1, md: 2 },
  minWidth: 0,
  position: { md: "sticky" },
  top: { md: 24 },
};

export const card =
  (elevation: "main" | "summary" = "main"): SxProps<Theme> =>
  (theme) => ({
    bgcolor: "background.paper",
    border: 1,
    borderColor: "divider",
    borderRadius: 2.75,
    boxShadow: elevation === "main" ? theme.shadows[3] : theme.shadows[1],
    p: { xs: 2, md: 3.25 },
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.25),
  });

export const stepBody: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  animation: `${fadeUp} 220ms ease-out`,
});

export const sectionTitle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontWeight: 700,
  fontSize: "0.875rem",
};

export const countChip: SxProps<Theme> = (theme) => ({
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "text.secondary",
  bgcolor: theme.palette.background.default,
  borderRadius: 999,
  px: 1,
  py: 0.25,
});

export const formStack: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
});

export const formGrid: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: theme.spacing(2),
});

export const divider: SxProps<Theme> = {
  height: 1,
  bgcolor: "divider",
};

export const primaryAction: SxProps<Theme> = {
  minHeight: 52,
};

export const formSection: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.75),
  "& + &": {
    pt: 2.25,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
});

export const formSectionHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
};

export const formSectionTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 800,
  color: "text.primary",
};

// Primary fills the row; cancel stays compact beside it (RTL: primary on the right).
export const actionRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "stretch",
  gap: theme.spacing(1.25),
  pt: 0.5,
});

export const actionPrimary: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
};

export const actionSecondary: SxProps<Theme> = (theme) => ({
  flex: "none",
  minWidth: { xs: 96, sm: 120 },
  minHeight: 52,
  px: 2.5,
  borderRadius: 1.2,
  borderColor: theme.palette.divider,
  color: "text.secondary",
  fontWeight: 700,
  boxShadow: "none",
  "&:hover": {
    borderColor: theme.palette.text.secondary,
    bgcolor: alpha(theme.palette.text.primary, 0.03),
  },
});

export const textAction: SxProps<Theme> = {
  color: "text.secondary",
  fontWeight: 600,
};

// ---------- Stepper ----------

export const stepper: SxProps<Theme> = (theme) => ({
  listStyle: "none",
  m: 0,
  p: 0,
  pb: 2,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  borderBottom: `1px dashed ${theme.palette.divider}`,
});

export const stepItem =
  (state: StepState): SxProps<Theme> =>
  (theme) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.75,
    position: "relative",
    "&:not(:last-of-type)::after": {
      content: '""',
      position: "absolute",
      top: 14,
      insetInlineStart: "calc(50% + 20px)",
      insetInlineEnd: "calc(-50% + 20px)",
      height: 2,
      borderRadius: 1,
      bgcolor: state === "done" ? theme.palette.primary.main : theme.palette.divider,
      transition: "background-color 0.3s ease",
    },
  });

export const stepDot =
  (state: StepState): SxProps<Theme> =>
  (theme) => ({
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 700,
    transition: "all 0.25s ease",
    color: state === "upcoming" ? theme.palette.text.secondary : theme.palette.primary.contrastText,
    bgcolor: state === "upcoming" ? theme.palette.background.paper : theme.palette.primary.main,
    border: `1.5px solid ${state === "upcoming" ? theme.palette.divider : theme.palette.primary.main}`,
    boxShadow: state === "current" ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}` : "none",
  });

export const stepLabel =
  (state: StepState): SxProps<Theme> =>
  () => ({
    fontSize: "0.6875rem",
    fontWeight: state === "current" ? 700 : 500,
    color: state === "upcoming" ? "text.secondary" : "text.primary",
    textAlign: "center",
  });

const stepInner = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.75,
  // Above the connector line drawn by stepItem::after.
  position: "relative",
  zIndex: 1,
} as const;

export const stepStatic: SxProps<Theme> = stepInner;

export const stepButton: SxProps<Theme> = (theme) => ({
  ...stepInner,
  border: "none",
  bgcolor: "transparent",
  p: 0.5,
  m: -0.5,
  borderRadius: 1.5,
  cursor: "pointer",
  fontFamily: "inherit",
  color: "inherit",
  transition: "background-color 0.2s ease",
  "& > :first-of-type": { transition: "transform 0.2s ease, box-shadow 0.2s ease" },
  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
  "&:hover > :first-of-type": {
    transform: "scale(1.08)",
    boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  "&:hover > :last-of-type": { color: theme.palette.primary.main, textDecoration: "underline" },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

export const stepHeading: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
};

export const stepIcon: SxProps<Theme> = (theme) => ({
  width: 44,
  height: 44,
  borderRadius: 1.5,
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: alpha(theme.palette.primary.main, 0.08),
});

export const stepTitle: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: "0.9375rem",
  lineHeight: 1.6,
};

export const stepDescription: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
  lineHeight: 1.9,
};

export const stepHint: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
  textAlign: "center",
};

export const stepError: SxProps<Theme> = (theme) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "error.main",
  textAlign: "center",
  lineHeight: 1.7,
  bgcolor: alpha(theme.palette.error.main, 0.06),
  borderRadius: 1.25,
  px: 1.5,
  py: 1,
});

export const inlineLink: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  border: "none",
  bgcolor: "transparent",
  p: 0,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
  "&:hover": { textDecoration: "underline" },
});

// ---------- OTP ----------

export const otpWrap: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  direction: "ltr",
  py: 1,
};

export const resendRow: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
};

export const devCode: SxProps<Theme> = (theme) => ({
  alignSelf: "center",
  fontSize: "0.71875rem",
  color: "text.secondary",
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 1,
  px: 1.25,
  py: 0.5,
});

// ---------- Details ----------

export const verifiedPhoneRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  fontSize: "0.75rem",
  color: "text.secondary",
  bgcolor: alpha(theme.palette.success.main, 0.06),
  border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
  borderRadius: 1.5,
  px: 1.5,
  py: 1.125,
});

export const mapBlock: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

export const mapHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const mapTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const mapStatus =
  (state: "idle" | "ok" | "error"): SxProps<Theme> =>
  () => ({
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: state === "error" ? "warning.main" : state === "ok" ? "success.main" : "text.secondary",
  });

export const suggestionBox: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  p: 1.5,
  borderRadius: 1.5,
  bgcolor: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
});

export const suggestionTitle: SxProps<Theme> = {
  fontSize: "0.75rem",
  fontWeight: 700,
};

export const suggestionText: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
  lineHeight: 1.8,
};

export const suggestionActions: SxProps<Theme> = {
  display: "flex",
  gap: 1,
};

// ---------- Payment ----------

export const shippingCard: SxProps<Theme> = (theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  overflow: "hidden",
  "& > div:first-of-type:not(:last-child)": { borderRadius: 0, border: "none" },
});

export const shippingBody: SxProps<Theme> = {
  p: 1.75,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
};

export const shippingHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 0.5,
};

export const shippingTitle: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const shippingName: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  fontSize: "0.84375rem",
  fontWeight: 700,
};

export const shippingPhone: SxProps<Theme> = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "text.secondary",
};

export const shippingLine: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
  lineHeight: 1.8,
};

export const securityNote: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.25),
  fontSize: "0.71875rem",
  color: "text.secondary",
  lineHeight: 1.8,
  bgcolor: theme.palette.background.default,
  borderRadius: 1.5,
  p: 1.25,
});

export const desktopPayWrap: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
};

export const payButton: SxProps<Theme> = {
  minHeight: { xs: 52, md: 54 },
};

export const mobilePayDock: SxProps<Theme> = (theme) => ({
  display: { xs: "block", md: "none" },
  position: "fixed",
  insetInline: 0,
  bottom: 0,
  zIndex: theme.zIndex.appBar,
  px: 2,
  pt: 1.5,
  pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
  borderTop: 1,
  borderColor: "divider",
  bgcolor: alpha(theme.palette.background.paper, 0.96),
  backdropFilter: "blur(8px)",
});

export const payButtonContent: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
});

// ---------- Summary ----------

export const summaryDesktop: SxProps<Theme> = (theme) => ({
  display: { xs: "none", md: "flex" },
  flexDirection: "column",
  gap: theme.spacing(2),
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.75,
  boxShadow: theme.shadows[1],
  p: 2.5,
});

export const summaryMobile: SxProps<Theme> = (theme) => ({
  display: { xs: "block", md: "none" },
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.25,
  boxShadow: theme.shadows[1],
  overflow: "hidden",
});

export const summaryToggle: SxProps<Theme> = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  p: 1.5,
  border: "none",
  bgcolor: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  color: "inherit",
};

export const summaryToggleTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const summaryToggleTotal: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 800,
  color: "primary.main",
  whiteSpace: "nowrap",
};

export const chevron =
  (open: boolean): SxProps<Theme> =>
  () => ({
    display: "flex",
    transition: "transform 0.2s ease",
    transform: open ? "rotate(180deg)" : "none",
  });

export const summaryMobileBody: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.75),
  px: 1.5,
  pb: 1.75,
  borderTop: `1px dashed ${theme.palette.divider}`,
  pt: 1.5,
});

export const thumbStack: SxProps<Theme> = {
  display: "flex",
  flex: "none",
};

export const thumbStackItem =
  (index: number): SxProps<Theme> =>
  (theme) => ({
    marginInlineStart: index === 0 ? 0 : -1.25,
    borderRadius: 1.25,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    zIndex: 3 - index,
  });

export const itemList: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
});

export const itemRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
});

export const itemThumb: SxProps<Theme> = {
  position: "relative",
  flex: "none",
};

export const qtyBadge: SxProps<Theme> = (theme) => ({
  position: "absolute",
  top: -6,
  insetInlineEnd: -6,
  minWidth: 20,
  height: 20,
  px: 0.5,
  borderRadius: 999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.625rem",
  fontWeight: 700,
  color: "primary.contrastText",
  bgcolor: "primary.main",
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
});

export const itemText: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const itemName: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const itemMeta: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const itemPrice: SxProps<Theme> = {
  fontSize: "0.78125rem",
  fontWeight: 700,
  flex: "none",
};

export const totals: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  bgcolor: theme.palette.background.default,
  borderRadius: 1.75,
  p: 1.5,
});

export const payRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.78125rem",
};

export const totalRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.875rem",
};

export const totalAmount: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: "1rem",
  color: "primary.main",
};

export const trustRow: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(1),
  pt: 0.5,
});

export const trustItem: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.5,
  textAlign: "center",
  fontSize: "0.65625rem",
  fontWeight: 600,
  color: "text.secondary",
  lineHeight: 1.5,
};

// ---------- Status screens ----------

export const spinner =
  (theme: Theme): SxProps<Theme> =>
  ({
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: `4px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderTopColor: theme.palette.primary.main,
    animation: `${spin} 0.8s linear infinite`,
  });

export const statusCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: theme.shadows[3],
  px: { xs: 2, sm: 3 },
  py: { xs: 6, sm: 7.5 },
  maxWidth: 520,
  mx: "auto",
  mt: { xs: 2, sm: 4 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.75),
  textAlign: "center",
});

export const statusIconWrap =
  (variant: "success" | "error"): SxProps<Theme> =>
  (theme) => ({
    width: { xs: 72, sm: 84 },
    height: { xs: 72, sm: 84 },
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 0.75,
    bgcolor:
      variant === "success"
        ? alpha(theme.palette.success.main, 0.1)
        : alpha(theme.palette.error.main, 0.1),
  });

export const statusTitle: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "1.03125rem", sm: "1.25rem" },
};

export const statusDescription: SxProps<Theme> = {
  fontSize: "0.8125rem",
  color: "text.secondary",
  lineHeight: 1.8,
  maxWidth: 380,
};

export const expiredIconWrap: SxProps<Theme> = (theme) => ({
  width: 72,
  height: 72,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: alpha(theme.palette.warning.main, 0.12),
  mb: 0.5,
});

export const shippingMethodGrid: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(160px, 1fr))" },
  gap: 1,
};

export const shippingMethodOption =
  (selected: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    p: 1.5,
    borderRadius: 1.5,
    border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    bgcolor: selected ? alpha(theme.palette.primary.main, 0.05) : "background.paper",
    cursor: "pointer",
    textAlign: "start",
    fontFamily: "inherit",
    color: "inherit",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": { borderColor: theme.palette.primary.main },
  });

export const shippingMethodTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const shippingMethodDesc: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};
